import { SearchResultsExecutionStatus } from '@as-react/common-types';
import {
  AlternativeQueryResult,
  BannerDTO,
  ColourDTO,
  FilterDTO,
  ListerDTO,
  ProductDTO,
  SearchPassDTO,
} from '@as-react/common-types/product-search-service';

const getDefaultProductColor = (product: ProductDTO) =>
  product.colours?.find(colour => colour.colourId === product.colourId);

const isProductColorAvailable = (color: ColourDTO): boolean => color.availability !== 0;

const isProductColorDisplayable = (colour: ColourDTO) =>
  isProductColorAvailable(colour) || colour.alwaysVisible || false;

const isDefaultProductColorAvailable = (product: ProductDTO): boolean => {
  const defaultColour = getDefaultProductColor(product);

  if (!defaultColour) {
    return false;
  }

  return isProductColorDisplayable(defaultColour);
};

const isNotTheDefaultProductColor = (defaultColour?: ColourDTO) => {
  return (colour: ColourDTO) => colour.colourId !== defaultColour?.colourId;
};

const removeInvalidProductColours = (product: ProductDTO): ProductDTO => {
  const defaultColour = getDefaultProductColor(product);
  const filteredColours =
    product.colours
      ?.filter(isNotTheDefaultProductColor(defaultColour))
      .filter(colour => isProductColorDisplayable(colour)) || [];

  return {
    ...product,
    colours: defaultColour ? [defaultColour, ...filteredColours] : filteredColours,
  };
};

const filterProductData = (searchResult: ListerDTO): ListerDTO => {
  const filteredSearchItems = searchResult.items
    ?.filter(product => isDefaultProductColorAvailable(product))
    .map(product => removeInvalidProductColours(product));

  return {
    ...searchResult,
    items: filteredSearchItems,
  };
};

/*
 * Check if default filter is set to Brand
 */
const isDefaultFilterBrand = (filterName?: string): boolean => {
  if (!filterName) {
    return false;
  }

  return filterName.includes('brand');
};

/*
 * Check if default filter is set to Brand
 */
const removeBrandFilterIfAny = (productSearchObject: ListerDTO): Array<FilterDTO> => {
  return productSearchObject.filters!.filter(item => {
    return item.filterOn !== 'brand';
  });
};

/*
 * Check if default filter is set to Brand
 */
const removeBrandFilterIfBrandPage = (productSearchObject: ListerDTO, filterName?: string): Array<FilterDTO> => {
  if (isDefaultFilterBrand(filterName)) {
    return removeBrandFilterIfAny(productSearchObject);
  }
  return productSearchObject.filters!;
};

export type ListerItemDTO = ProductDTO | BannerDTO;
const mergeBannersInProductArray = (searchResult: ListerDTO): Array<ListerItemDTO> => {
  let items: Array<ListerItemDTO> = [];
  if (searchResult.items) {
    items = [...searchResult.items!];
  }

  if (searchResult.banners) {
    searchResult.banners.reverse().forEach(banner => {
      if (!!banner.listerPosition && items.length >= banner.listerPosition) {
        const positionIndex = banner.listerPosition - 1;
        items = [...items.slice(0, positionIndex), banner, ...items.slice(positionIndex)];
      }
    });
  }

  return items;
};

export type ProductSearchDTO = ListerDTO & {
  items?: Array<ListerItemDTO>;
  filters?: Array<FilterDTO>;
  totalPages?: number;
  itemCount?: number;
  banners?: Array<BannerDTO>;
  redirect?: boolean;
  redirectUrl?: string;
  segment?: string;
  executionStatus?: SearchResultsExecutionStatus;
  alternativeQueryResults?: Array<AlternativeQueryResult>;
  traits?: Array<string>;
  searchPasses?: Array<SearchPassDTO>;
  personalized?: boolean;
};
export const handleProductSearchObject = (searchResult: ListerDTO, defaultFilter?: string): ProductSearchDTO => {
  const filteredProductSearchObject = filterProductData(searchResult);
  return {
    ...filteredProductSearchObject,
    executionStatus: filteredProductSearchObject.executionStatus as SearchResultsExecutionStatus | undefined,
    items: mergeBannersInProductArray(filteredProductSearchObject),
    filters: removeBrandFilterIfBrandPage(filteredProductSearchObject, defaultFilter),
  };
};
